import { useEffect, useState } from "react";
import { formatShopifyMoney, sanitizeHtml } from "../../helpers/helpers.tsx";
import styles from "../styles.module.scss";
import { getActivePromoCode, fetchGiftSetBundleDisplay, type BundleDisplay } from "../../services/GiftSetService";

declare global {
    interface Window {
        scs_sweepstakes_tooltip_text?: string;
        scs_father_day_2026?: boolean;
        scs_father_day_2026_warranty_text?: string;
        scs_father_day_2026_warranty_msrp?: string;
        scs_father_day_2026_warranty_badge?: string;
        scs_father_day_2026_warranty_icon?: string;
        scs_father_day_2026_warranty_icon_img?: string;
        scs_warranty_row?: boolean;
    }
}

const parsePrice = (price?: string): number => parseInt((price ?? '0').replace('$', '')) || 0;

export default function SuperOffer(props) {
    const [bundleOverride, setBundleOverride] = useState<BundleDisplay | null>(null);

    useEffect(() => {
        const promoCode = getActivePromoCode();
        if (!promoCode) return;
        let cancelled = false;
        fetchGiftSetBundleDisplay(promoCode).then((data) => {
            if (!cancelled) setBundleOverride(data);
        });
        return () => { cancelled = true; };
    }, []);

    let scs_black_friday_2025_h2 = window.scs_black_friday_2025_h2;
    if (scs_black_friday_2025_h2 && scs_black_friday_2025_h2.includes('$BONUS_VALUE')) {
        const bonusValue = parsePrice(window.scs_promo_bonus_1_price)
        + parsePrice(window.scs_promo_bonus_2_price)
        + parsePrice(window.scs_promo_bonus_3_price)
        + parsePrice(window.scs_promo_bonus_4_price);
        
        scs_black_friday_2025_h2 = scs_black_friday_2025_h2.replace('$BONUS_VALUE', formatShopifyMoney(bonusValue));
    }

    if (window.scs_sweepstakes_tooltip_text && scs_black_friday_2025_h2 && scs_black_friday_2025_h2.includes("[ST]")) {
        const tooltipText = (window.scs_sweepstakes_tooltip_text || "").replace(/'/g, "&#39;");
        scs_black_friday_2025_h2 = scs_black_friday_2025_h2.replace(
            /\[ST\](.*?)\[\/ST\]/,
            (_, captured) => `<span class='scs-sweepstakes-tooltip' data-tooltip='${tooltipText}'>${captured}</span>`
        );
    }

    if (scs_black_friday_2025_h2) {
        scs_black_friday_2025_h2 = scs_black_friday_2025_h2
            .replace(/\[ST\]/g, '<strong>')
            .replace(/\[\/ST\]/g, '</strong>')
            .replace(/\[EM\]/g, '<em>')
            .replace(/\[\/EM\]/g, '</em>');
    }

    // When a gift-set promo is active, GiftBundleStrip (rendered after the Add
    // To Cart button in StepNav) shows its gifts instead — this box stays hidden
    // so the promo isn't advertised twice with two different designs.
    if (!window.scs_black_friday_2025 || bundleOverride) return '';

    // Liquid sometimes ships "$0.00" instead of a blank string for an unset
    // price setting, so treat a parsed value of 0 as "no price" too.
    const price1 = parsePrice(window.scs_promo_bonus_1_price);
    const price2 = parsePrice(window.scs_promo_bonus_2_price);
    const price3 = parsePrice(window.scs_promo_bonus_3_price);
    const price4 = parsePrice(window.scs_promo_bonus_4_price);

    return window.scs_black_friday_2025 ? <>
        {window.scs_new_design_2026 ? '' :
            <h4 className={`${styles.bf_heading}`} dangerouslySetInnerHTML={{__html: sanitizeHtml(window.scs_black_friday_2025_h1)}}></h4>}
        <div className={`${styles.bf_wrapper} ${window.scs_father_day_2026 ? styles.bf_wrapper_father_day_2026 : ''}`}>
            <h5 dangerouslySetInnerHTML={{__html: sanitizeHtml(scs_black_friday_2025_h2)}}></h5>
            <ul className={`${styles.bf_items}`}>
                {(window.scs_promo_bonus_1_img || window.scs_promo_bonus_1_title || price1 > 0) && (
                    <li>
                        {window.scs_promo_bonus_1_img && (
                            <div className={`${styles.img_wrapper}`}>
                                <img src={`${window.scs_promo_bonus_1_img}`} alt={`${window.scs_promo_bonus_1_alt}`}/>
                            </div>
                        )}
                        {window.scs_promo_bonus_1_title && (
                            <strong dangerouslySetInnerHTML={{__html: sanitizeHtml(window.scs_promo_bonus_1_title)}}></strong>
                        )}
                        {price1 > 0 && (
                            <span>MSRP: <s data-testid="promo-bonus-1-msrp">{formatShopifyMoney(price1)}</s></span>
                        )}
                    </li>
                )}
                {(window.scs_promo_bonus_2_img || window.scs_promo_bonus_2_title || price2 > 0) && (
                    <li>
                        {window.scs_promo_bonus_2_img && (
                            <div className={`${styles.img_wrapper}`}>
                                <img src={`${window.scs_promo_bonus_2_img}`} alt={`${window.scs_promo_bonus_2_alt}`}/>
                            </div>
                        )}
                        {window.scs_promo_bonus_2_title && (
                            <strong dangerouslySetInnerHTML={{__html: sanitizeHtml(window.scs_promo_bonus_2_title)}}></strong>
                        )}
                        {price2 > 0 && (
                            <span>MSRP: <s data-testid="promo-bonus-2-msrp">{formatShopifyMoney(price2)}</s></span>
                        )}
                    </li>
                )}
                {(window.scs_promo_bonus_3_img || window.scs_promo_bonus_3_title || price3 > 0) && (
                    <li>
                        {window.scs_promo_bonus_3_img && (
                            <div className={`${styles.img_wrapper}`}>
                                <img src={`${window.scs_promo_bonus_3_img}`} alt={`${window.scs_promo_bonus_3_alt}`}/>
                            </div>
                        )}
                        {window.scs_promo_bonus_3_title && (
                            <strong dangerouslySetInnerHTML={{__html: sanitizeHtml(window.scs_promo_bonus_3_title)}}></strong>
                        )}
                        {price3 > 0 && (
                            <span>MSRP: <s data-testid="promo-bonus-3-msrp">{formatShopifyMoney(price3)}</s></span>
                        )}
                    </li>
                )}
                {(window.scs_promo_bonus_4_img || window.scs_promo_bonus_4_title || price4 > 0) && (
                    <li>
                        {window.scs_promo_bonus_4_img && (
                            <div className={`${styles.img_wrapper}`}>
                                <img src={`${window.scs_promo_bonus_4_img}`} alt={`${window.scs_promo_bonus_4_alt}`}/>
                            </div>
                        )}
                        {window.scs_promo_bonus_4_title && (
                            <strong dangerouslySetInnerHTML={{__html: sanitizeHtml(window.scs_promo_bonus_4_title)}}></strong>
                        )}
                        {price4 > 0 && (
                            <span>MSRP: <s data-testid="promo-bonus-4-msrp">{formatShopifyMoney(price4)}</s></span>
                        )}
                    </li>
                )}
            </ul>
            {window.scs_warranty_row ? (
                <div className={`${styles.bf_warranty_bar}`}>
                    <span className={`${styles.bf_warranty_icon}`} aria-hidden="true">
                        {window.scs_father_day_2026_warranty_icon_img ? (
                            <img className={`${styles.bf_warranty_icon_img}`} src={`${window.scs_father_day_2026_warranty_icon_img}`} alt=""/>
                        ) : (
                            window.scs_father_day_2026_warranty_icon || '📜'
                        )}
                    </span>
                    <span className={`${styles.bf_warranty_text}`}>
                        {window.scs_father_day_2026_warranty_text || '3 YEAR WARRANTY'}
                    </span>
                    <span className={`${styles.bf_warranty_msrp}`}>
                        MSRP: <s>{window.scs_father_day_2026_warranty_msrp || '$100'}</s>
                    </span>
                    <span className={`${styles.bf_warranty_badge}`}>
                        {window.scs_father_day_2026_warranty_badge || 'INCLUDED'}
                    </span>
                </div>
            ) : null}
        </div>
        <h6 className={`${styles.bf_choose_heading}`}><span>Choose Your Setup</span></h6>

    </> : '';
}